import { useNavigate, useSearchParams } from 'react-router';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCountdown, useToast } from '@hostfully/ui-components';
import buildAuthSuccessRedirectUrl from '../buildAuthSuccessRedirectUrl';
import buildMfaUrl from '../buildMfaUrl';
import { GRANT_TYPE_PARAM } from '../Login.constants';
import useLoginUrlContext from '../useLoginUrlContext';
import {
  MFA_RESEND_COOLDOWN_SECONDS,
  MFA_RESEND_COOLDOWN_STORAGE_KEY,
} from './MFA.constants';
import useMFAExchangeMutation, {
  type MFAExchangeSubmitValues,
} from './queries/useMFAExchangeMutation';
import useMFAResendMutation from './queries/useMFAResendMutation';
import {
  AuthenticateStatus,
  type AuthenticateResponse,
} from '@/services/login.types';
import { Location } from '@/utils/windowsUtils';

interface UseMFASubmitArgs {
  mfaCode: string;
}

const PIN_FIELD = 'pinCode';

const useMFASubmit = ({ mfaCode }: UseMFASubmitArgs) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { pkce, appContext } = useLoginUrlContext();
  const [searchParams] = useSearchParams();
  const grantType = searchParams.get(GRANT_TYPE_PARAM);
  const { setError, clearErrors } = useFormContext();
  const cooldown = useCountdown({
    storageKey: MFA_RESEND_COOLDOWN_STORAGE_KEY,
    initialSeconds: MFA_RESEND_COOLDOWN_SECONDS,
  });

  const setPinError = (message: string) =>
    setError(PIN_FIELD, { type: 'manual', message });

  const handleAuthResponse = (data: AuthenticateResponse) => {
    if (data.status === AuthenticateStatus.SUCCESSFUL) {
      if (!pkce) {
        setPinError(t('pageLogin.anotherPinCodeExpected'));
        return;
      }
      Location.change(
        buildAuthSuccessRedirectUrl({
          redirectUri: pkce.redirectUri,
          code: data.code,
          state: data.state,
          requestedPath: appContext.requestedPath,
          grantType,
        }),
      );
      return;
    }

    if (data.status === AuthenticateStatus.MFA_CHALLENGE) {
      if (!pkce) {
        setPinError(t('pageLogin.anotherPinCodeExpected'));
        return;
      }
      navigate(
        buildMfaUrl({
          pkce,
          appContext,
          mfaCode: data.mfaCode,
          mfaCodeSentTo: data.mfaCodeSentTo,
          mfaType: data.mfaType,
          grantType,
        }),
        { replace: true },
      );
      return;
    }

    setPinError(t('pageLogin.anotherPinCodeExpected'));
  };

  const exchangeMutation = useMFAExchangeMutation({
    mfaCode,
    onSuccess: handleAuthResponse,
    onError: () => setPinError(t('pageLogin.anotherPinCodeExpected')),
  });

  const resendMutation = useMFAResendMutation({
    mfaCode,
    onSuccess: (data) => {
      cooldown.start(MFA_RESEND_COOLDOWN_SECONDS);
      toast.success({ title: t('pageLogin.codeResentSuccess') });

      if (data.status === AuthenticateStatus.MFA_CHALLENGE) {
        if (!pkce) {
          return;
        }
        navigate(
          buildMfaUrl({
            pkce,
            appContext,
            mfaCode: data.mfaCode,
            mfaCodeSentTo: data.mfaCodeSentTo,
            mfaType: data.mfaType,
            grantType,
          }),
          { replace: true },
        );
      }
    },
    onError: () => {
      toast.error({ title: t('pageLogin.codeResentError') });
    },
  });

  const handleSubmitMFA = (values: MFAExchangeSubmitValues) => {
    clearErrors(PIN_FIELD);
    exchangeMutation.mutate(values);
  };

  const handleResendCode = () => {
    clearErrors(PIN_FIELD);
    resendMutation.mutate();
  };

  return {
    handleSubmitMFA,
    handleResendCode,
    isSubmitting: exchangeMutation.isPending,
    isResending: resendMutation.isPending,
    resendCooldownSeconds: cooldown.remainingSeconds,
  };
};

export default useMFASubmit;
